import {Heading, Text} from 'platform/foundation-tw';
import {match} from 'ts-pattern';

import type {ReactNode} from 'react';

export type BannerTone = 'warning' | 'error' | 'info';

interface BannerToneClasses {
  container: string;
  title: string;
  body: string;
}

const TONE_CLASSES: Record<BannerTone, BannerToneClasses> = {
  warning: {
    container: 'rounded-lg border border-amber-200 bg-amber-50 px-6 py-4',
    title: 'text-amber-800',
    body: 'text-amber-700',
  },
  error: {
    container: 'rounded-lg border border-red-200 bg-red-50 px-6 py-4',
    title: 'text-red-800',
    body: 'text-red-700',
  },
  info: {
    container: 'rounded-lg border border-blue-200 bg-blue-50 px-6 py-4',
    title: 'text-blue-800',
    body: 'text-blue-700',
  },
};

interface BannerProps {
  tone: BannerTone;
  title: string;
  description?: ReactNode;
  /** Optional `data-testid` for E2E. */
  testId?: string;
}

/**
 * Themed alert banner shared across Mira's report flows. Composes
 * platform-tw `Heading` / `Text` for typography, with a tone-coloured
 * Tailwind container.
 */
export function Banner(props: BannerProps) {
  const classes = match(props.tone)
    .with('warning', () => TONE_CLASSES.warning)
    .with('error', () => TONE_CLASSES.error)
    .with('info', () => TONE_CLASSES.info)
    .exhaustive();

  return (
    <div role="alert" className={classes.container} data-testid={props.testId}>
      <div className={classes.title}>
        <Heading size={4}>{props.title}</Heading>
      </div>
      {props.description != null ? (
        <div className={`mt-2 ${classes.body}`}>
          <Text size="small">{props.description}</Text>
        </div>
      ) : null}
    </div>
  );
}
